import { useCallback } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useStore } from '../stores/useStore';

export type ShareTarget =
  | { type: 'geometry'; id: string }
  | { type: 'study'; id: string };

/**
 * Centralised share-link logic for meshgen geometries and simops studies.
 *
 * Builds the public URL from the TanStack Router route definition so that
 * any future route rename is caught by TypeScript and only requires updating
 * this one file. Points at the public, unauthenticated `/share/...` viewer
 * routes (ENG-1067) rather than the in-app editor routes — a copied link
 * must work for a signed-out recipient.
 */
export function useShareLink() {
  const router = useRouter();
  const showToast = useStore((s) => s.showToast);
  const selectedMeshId = useStore((s) => s.selectedMeshId);

  const copyShareLink = useCallback(
    (target: ShareTarget) => {
      const path =
        target.type === 'geometry'
          ? router.buildLocation({
              to: '/share/geometry/$uuid',
              params: { uuid: target.id },
              // Carry the currently-selected mesh (ENG-1071) so "share this"
              // lands the viewer on the same mesh the sharer was looking at,
              // not the geometry's latest-completed default. Read from the
              // global store rather than threading a param through every
              // `copyShareLink` call site — this is correct at each of them:
              // called from inside the MeshGen workspace, `selectedMeshId` IS
              // the mesh on screen; called from the dashboard (no workspace
              // mounted), it's null/stale-irrelevant and the share route
              // falls back to its own latest-completed default, which is the
              // right behavior for "share this project" with no specific
              // mesh in view. Study links are unaffected — see the `study`
              // branch below, deliberately untouched.
              search: selectedMeshId ? { mesh: selectedMeshId } : undefined,
            }).href
          : router.buildLocation({
              to: '/share/study/$studyId',
              params: { studyId: target.id },
            }).href;
      const url = `${window.location.origin}${path}`;
      void navigator.clipboard.writeText(url);
      showToast('Link copied to clipboard');
    },
    [router, showToast, selectedMeshId],
  );

  return { copyShareLink };
}
